/** @format */

import { Fragment, useState } from 'react';
import map from 'lodash/map';
import { useDispatch } from 'src/store';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import Slider from 'react-slick';
import { useTranslation } from 'react-i18next';
import useAuth from 'src/hooks/useAuth';
import { completeOnBoarding } from 'src/slices/users';
import { Button } from 'src/components/button';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const OnBoardingView = ({ onCloseModal }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [slideRef, setSlideRef] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const { user, uuid, updateOnBoardingComplete } = useAuth();
  const { t } = useTranslation();

  const handleCloseOrFinish = async () => {
    const isCompleted = Boolean(user?.onBoardingComplete);

    if (isCompleted) {
      navigate(`/dashboard`);
      onCloseModal?.();
      return;
    }

    const success = await dispatch(completeOnBoarding(user?.uuid));
    if (!success) return;

    updateOnBoardingComplete();
    navigate('/dashboard');
    onCloseModal?.();
  };

  const handleNext = () => {
    slideRef?.slickNext();
  };

  const handlePrev = () => {
    slideRef?.slickPrev();
  };

  const sliderSettings = {
    centerMode: true,
    accessibility: false,
    fade: true,
    slidesToShow: 1,
    dots: true,
    arrows: false,
    swipeToSlide: false,
    focusOnSelect: false,
    infinite: false,
    autoplay: false,
    draggable: false,
    beforeChange: (current, next) => setActiveStep(next),
    centerPadding: '0'
  };

  const slides = [
    {
      step: 1,
      title: t('onBoarding.slide_1.title'),
      content: t('onBoarding.slide_1.content'),
      image: '/static/images/onboarding/step1.png'
    },
    {
      step: 2,
      title: t('onBoarding.slide_2.title'),
      content: t('onBoarding.slide_2.content'),
      image: '/static/images/onboarding/step2.png'
    },
    {
      step: 3,
      title: t('onBoarding.slide_3.title'),
      content: t('onBoarding.slide_3.content'),
      image: '/static/images/onboarding/step3.png'
    },
    {
      step: 4,
      title: t('onBoarding.slide_4.title'),
      content: t('onBoarding.slide_4.content'),
      image: '/static/images/onboarding/step4.png'
    },
    {
      step: 5,
      title: t('onBoarding.slide_5.title'),
      content: t('onBoarding.slide_5.content'),
      image: '/static/images/onboarding/step5.png'
    },
    {
      step: 6,
      title: t('onBoarding.slide_6.title'),
      content: t('onBoarding.slide_6.content'),
      image: '/static/images/onboarding/step6.png'
    }
  ];

  const lastStepIndex = slides.length - 1;
  const isLastStep = activeStep === lastStepIndex;

  return (
    <Fragment>
      <div className="onboarding-cover onboarding-cover--modal">
        <Box>
          <Box className="close-onboard">
            <button onClick={handleCloseOrFinish} className="close-btn">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <g clipPath="url(#clip0_986_28360)">
                  <path
                    d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z"
                    fill="#161616"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_986_28360">
                    <rect width="24" height="24" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>
          </Box>

          <Box className="onboarding-wrapper">
            <Slider {...sliderSettings} className="slide2" ref={slider => setSlideRef(slider)}>
              {map(slides, item => (
                <Box key={item.step} className="onboard-content">
                  <h3>{item.title}</h3>
                  <p>{item.content}</p>
                  <div className="onboard-content__image">
                    <img src={item.image} alt="" />
                  </div>
                </Box>
              ))}
            </Slider>

            <Box className="footer-btn">
              {activeStep > 0 && <Button type="button" label={t('common.button.backbtn')} ghost onClick={handlePrev} />}
              {isLastStep ? (
                <Button type="button" label={t('onBoarding.finish')} onClick={handleCloseOrFinish} small check />
              ) : (
                <Button type="button" label={t('onBoarding.next')} onClick={handleNext} small />
              )}
            </Box>
          </Box>
        </Box>
      </div>
    </Fragment>
  );
};

export default OnBoardingView;
